import React, { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";
import { connect } from "react-redux";
import defaultTheme from "../../theme/default";
import {
  masks as themeMasks,
  unmasks as themeUnmasks,
} from "../../theme/default/masks";
import { MessagingLayer } from "../../context/messaging-layer";
import { ToastNotifications } from "../../context/toast-notifications";
import { RootContext } from "../../context/root-ref";
import { constants } from "../../resource/constants";
import { createFacilitiesFetcher } from "../../api/facilities";
import showFilterIcon from "../../styles/default/assets/icons/controls-setup-preferences.svg";
import FacilityItem from "./facility-item";
import Toasts from "../toast-notification";
import { addToast, removeToast } from "../../redux/actions";

const FACILITY_TOAST_ID = "__FIND_FACILITY__";

const facilityMessages = {
  NO_FACILITY: "No facility found. Please, update search params.",
  ZIP_REQUIRED: "Zip code is required.",
};

const itemsLimitPerPage = 25;

const TdMarker = ({
  facility,
  active,
  selected,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  const {
    distance,
    data: { company, address1, city, state, zip },
  } = facility;
  return (
    <div>
      <div
        className={`g-map-marker-web-sdk ${active || selected ? "active" : ""}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      />
      {selected && (
        <div className="g-map-tooltip-web-sdk">
          <div className="g-map-tooltip-header-web-sdk">{company}</div>
          <div className="websdk-flex websdk-justify-between g-map-tooltip-section-web-sdk">
            <div>{address1}</div>
            <div>{distance.toFixed(2)} mi</div>
          </div>
          <div className="g-map-tooltip-section-web-sdk">
            {city}, {state} {zip}
          </div>
        </div>
      )}
    </div>
  );
};

const FindFacilities = ({
  extendedConfiguration,
  addToast,
  theme = {},
  fieldConfigurations,
  toasts,
  removeToast,
  screenParams,
}) => {
  const rootRef = useRef();
  const facilityListRef = useRef();
  const [inputsValues, setInputsValues] = useState({
    radius: 10,
    kind: extendedConfiguration?.findFacilities?.facilityType
      ? [extendedConfiguration.findFacilities.facilityType]
      : constants.facilityTypes.map((type) => type.value),
    page: 0,
  });
  const [facilitiesData, setFacilitiesData] = useState([]);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  const [isLastPage, setIsLastPage] = useState(false);
  const [isFiltersCollapsed, setFiltersCollapsed] = useState(true);
  const [activeItem, setActiveItem] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const favoriteList =
    extendedConfiguration?.findFacilities?.favoriteList || [];
  const markers = [];
  const formFetcher = createFacilitiesFetcher();
  const addToFavorite = extendedConfiguration?.findFacilities?.addToFavorite;
  const components = { ...defaultTheme, ...theme };
  let { masks, unmasks, fieldAttributes } = fieldConfigurations || {};
  masks = { ...masks, ...themeMasks };
  unmasks = { ...unmasks, ...themeUnmasks };
  fieldAttributes = fieldAttributes || {};

  let abortController;
  let gMaps;

  useEffect(() => {
    if (
      typeof inputsValues.zip === "undefined" &&
      extendedConfiguration?.findFacilities?.zipCode
    ) {
      onFieldChange(extendedConfiguration.findFacilities.zipCode, "zip");
    }
    facilityListRef?.current?.addEventListener("scroll", onScrollHandler);

    return () => {
      markers.forEach((m) => gMaps.event.clearInstanceListeners(m));
      facilityListRef?.current?.removeEventListener("scroll", onScrollHandler);
      abortController = window[constants.abortControllerKey];
      if (abortController) {
        abortController.abort();
      }
    };
  }, [inputsValues]);

  const extractSearchParams = (data) => {
    return (
      "?" +
      Object.keys(data)
        .filter(
          (k) =>
            data[k] ||
            ((typeof data[k] === "string" || typeof data[k] === "array") &&
              data[k].length)
        )
        .map(
          (k) =>
            `${k}=${typeof data[k] === "array" ? data[k].join(",") : data[k]}`
        )
        .join("&")
    );
  };

  const onRadiusChange = (value) => {
    onFieldChange(value, "radius");
  };

  const onTypeChange = (value) => {
    const index = inputsValues.kind.indexOf(value);
    let updatedArray;
    if (index !== -1) {
      updatedArray = [...inputsValues.kind];
      updatedArray.splice(index, 1);
    } else {
      updatedArray = [...inputsValues.kind, value];
    }
    onFieldChange(updatedArray, "kind");
  };

  const onZipChange = ({ currentTarget: { value } }, name) => {
    onFieldChange(value, name);
  };

  const onScrollHandler = () => {
    if (
      facilityListRef?.current?.scrollTop +
        facilityListRef?.current?.offsetHeight <
        facilityListRef?.current?.scrollHeight ||
      loadingInProgress
    ) {
      return;
    }
    fetchData(true);
  };

  const fetchData = (useScroll, values) => {
    values = values || inputsValues;
    if (useScroll && isLastPage) {
      return;
    }
    setLoadingInProgress(true);
    !useScroll && setFacilitiesData([]);

    const page = useScroll ? values.page + 1 : values.page;
    const searchParams = extractSearchParams({ ...values });
    formFetcher(searchParams).then((res) => {
      if (res?.data?.places) {
        if (res.data.places.length) {
          let newFacilitiesData = [...res.data.places];
          useScroll
            ? setFacilitiesData([...facilitiesData, ...newFacilitiesData])
            : setFacilitiesData([...newFacilitiesData]);
          setLoadingInProgress(false);
          setInputsValues({ ...values });
          newFacilitiesData.length < itemsLimitPerPage && setIsLastPage(true);
        } else {
          setLoadingInProgress(false);
          setIsLastPage(true);
        }
      } else {
        // TODO: There is an issue in the rp a new story is being created. Its not returning the correct search results.
        if (page === 0) {
          setFacilitiesData([]);
          showToast("NO_FACILITY");
          setInputsValues({ ...values, page });
        }
        setLoadingInProgress(false);
      }
    });
  };

  const onFieldChange = (value, name) => {
    const newInputsValues = { ...inputsValues, page: 0 };
    if (newInputsValues[name] !== value) {
      newInputsValues[name] = value;
      if (newInputsValues.zip) {
        setInputsValues(newInputsValues);
        setIsLastPage(false);
        if (constants.patterns.zipRegexp.test(newInputsValues.zip)) {
          fetchData(false, newInputsValues);
        }
      } else {
        setInputsValues(newInputsValues);
        showToast("ZIP_REQUIRED");
      }
    }
  };

  const showToast = (messageType) => {
    addToast({
      id: FACILITY_TOAST_ID,
      message: facilityMessages[messageType],
    });
  };

  const onClearFilters = () => {
    const {
      findFacilities: { zipCode, facilityType },
    } = extendedConfiguration;
    setInputsValues({
      zipCode,
      radius: 10,
      kind: facilityType,
      page: 0,
    });

    setFacilitiesData([]);
  };

  const wrapAddLabel = (text, child, className) => (
    <div className={className}>
      <components.Label text={text} />
      {child}
    </div>
  );

  const hoverOnFacilityItem = debounce((facility) => {
    const {
      id,
      location: [longitude, latitude],
    } = facility.data;
    setActiveItem(id);
    setSelectedItem(id === selectedItem ? "" : id);
    setMapCenter([latitude, longitude]);
  }, 250);

  const populateFacilityList = () => {
    const components = { ...defaultTheme, ...theme };
    let facilities = [...facilitiesData];
    return facilities.map((facility, i) => {
      const { id } = facility.data;
      return (
        <FacilityItem
          components={components}
          keyToAdd={i}
          key={i}
          active={id === activeItem}
          selected={id === selectedItem}
          onMouseEnter={() => hoverOnFacilityItem(facility)}
          onMouseLeave={() => setActiveItem("")}
          facilityData={facility}
          addToFavorite={addToFavorite}
          favoriteList={favoriteList}
        />
      );
    });
  };

  const handleMarkerClick = useCallback(
    (event, facility) => {
      const {
        id,
        location: [longitude, latitude],
      } = facility.data;
      event.stopPropagation();
      setSelectedItem(id === selectedItem ? "" : id);
      setMapCenter([latitude, longitude]);
    },
    [selectedItem]
  );

  const renderFacilitiesOnMap = useCallback(
    ({ map, maps: { LatLng, LatLngBounds } }) => {
      const bounds = new LatLngBounds();
      facilitiesData.forEach(
        ({
          data: {
            location: [longitude, latitude],
          },
        }) => {
          const lat = parseFloat(latitude);
          const lng = parseFloat(longitude);
          bounds.extend(new LatLng(lat, lng));
        }
      );
      map.fitBounds(bounds);
    },
    [facilitiesData]
  );

  return (
    <MessagingLayer
      components={components}
      globalDialogConfig={screenParams.dialogConfig}
    >
      <ToastNotifications components={components}>
        <RootContext.Provider value={rootRef}>
          <Toasts
            toasts={toasts}
            onClose={(toastId) => removeToast(toastId)}
            components={components}
          />
          <div className="find-facilities-content-web-sdk">
            <components.Title title="Find facilities" />
            <div className="find-facilities-wrapper-web-sdk">
              <div className="expando-header-web-sdk">
                <span
                  className="clickable"
                  onClick={() => setFiltersCollapsed(!isFiltersCollapsed)}
                >
                  Find and filter
                </span>
                {!isFiltersCollapsed ? (
                  <span
                    className="clickable"
                    onClick={() => setFiltersCollapsed(true)}
                  >
                    Collapse
                  </span>
                ) : (
                  <components.Icon
                    showIcon={true}
                    src={showFilterIcon}
                    onClick={() => setFiltersCollapsed(false)}
                  />
                )}
              </div>
              <div
                className={`find-facilities-web-sdk ${
                  isFiltersCollapsed ? "collapsed" : ""
                }`}
              >
                <form
                  id="containerForm"
                  className="find-facilities-container-web-sdk"
                  ref={rootRef}
                >
                  <div className="zip-input-container-web-sdk">
                    {wrapAddLabel(
                      "Zip",
                      <components.Input
                        onChange={onZipChange}
                        placeholder="Enter zip"
                        name="zip"
                        value={inputsValues.zip}
                      />,
                      "xs:websdk-w-full sm:websdk-w-115px"
                    )}
                  </div>
                  <div className="radius-select-container-web-sdk">
                    {wrapAddLabel(
                      "Radius (mi)",
                      <components.Select
                        onChange={onRadiusChange}
                        options={constants.radiusOptions}
                        name="radius"
                        fieldAttributes={constants.defaultRadiusOption}
                        value={inputsValues.radius}
                      />,
                      "xs:websdk-w-full sm:websdk-w-115px"
                    )}
                  </div>
                  <div className="facility-type-container-web-sdk">
                    {constants.facilityTypes.map(({ value, label }) => {
                      return (
                        <components.Button
                          label={label}
                          name={value}
                          key={value}
                          className={
                            inputsValues.kind.includes(value)
                              ? "facility-type pressed"
                              : "facility-type"
                          }
                          onClick={() => onTypeChange(value)}
                        />
                      );
                    })}
                  </div>
                </form>
                <div className="clear-web-sdk">
                  <components.Text
                    label="Clear filters"
                    onClick={onClearFilters}
                  />
                </div>
              </div>
            </div>
            {facilitiesData.length ? (
              <div className="facilities-search-results-web-sdk">
                <div ref={facilityListRef} className="facilities-list-web-sdk">
                  {populateFacilityList()}
                </div>
                <div
                  className={`facilities-map-web-sdk ${
                    !isFiltersCollapsed ? "hide-map" : ""
                  }`}
                  style={{ position: "sticky", top: 10 }}
                >
                  <components.GMap
                    renderLocations={renderFacilitiesOnMap}
                    center={mapCenter}
                  >
                    {facilitiesData.map((facility, i) => {
                      const {
                        id,
                        location: [longitude, latitude],
                      } = facility.data;
                      return (
                        <TdMarker
                          key={i}
                          facility={facility}
                          active={id === activeItem}
                          selected={id === selectedItem}
                          onMouseEnter={() => setActiveItem(id)}
                          onMouseLeave={() => setActiveItem("")}
                          onClick={(e) => handleMarkerClick(e, facility)}
                          lat={parseFloat(latitude)}
                          lng={parseFloat(longitude)}
                        />
                      );
                    })}
                  </components.GMap>
                </div>
              </div>
            ) : null}
            {loadingInProgress && (
              <div>
                <components.Loader />
              </div>
            )}
          </div>
        </RootContext.Provider>
      </ToastNotifications>
    </MessagingLayer>
  );
};

FindFacilities.propTypes = {
  addToast: PropTypes.func.isRequired,
  removeToast: PropTypes.func.isRequired,
  fieldConfigurations: PropTypes.object,
  toasts: PropTypes.array,
  theme: PropTypes.object,
  screenParams: PropTypes.shape({
    dialogConfig: PropTypes.object,
  }),
  extendedConfiguration: PropTypes.shape({
    findFacilities: PropTypes.shape({
      zipCode: PropTypes.string,
      radius: PropTypes.number,
      facilityType: PropTypes.string,
    }),
  }),
};

export default connect(
  ({ tdFlow }) => ({
    toasts: tdFlow.toasts,
    screenParams: tdFlow.screenParams,
  }),
  {
    addToast,
    removeToast,
  }
)(FindFacilities);
